import { Injectable, computed, Signal } from '@angular/core';
import { Device, getDeviceIcon } from './device.service';

@Injectable({
    providedIn: 'root'
})
export class DashboardDataService {

    constructor() { }

    // Widget 1: System Stats
    getSystemStats(devices: Signal<Device[]>) {
        return computed(() => {
            const all = devices();
            const total = all.length;
            const normal = all.filter(d => d.status === 'NORMAL').length;
            const alarm = all.filter(d => d.status === 'ALARM').length;
            const maintenance = all.filter(d => d.status === 'MAINTENANCE').length;
            const offline = all.filter(d => d.status === 'OFFLINE').length;

            const getPercent = (val: number) => total > 0 ? Math.round((val / total) * 100) : 0;

            return [
                { label: '總數', value: total, percent: 100, color: 'text-gray-800', bg: 'bg-gray-100' },
                { label: '正常', value: normal, percent: getPercent(normal), color: 'text-green-600', bg: 'bg-green-50' },
                { label: '異常', value: 0, percent: 0, color: 'text-gray-400', bg: 'bg-gray-50' },
                { label: '告警', value: alarm, percent: getPercent(alarm), color: 'text-red-600', bg: 'bg-red-50' },
                { label: '離線', value: offline, percent: getPercent(offline), color: 'text-gray-500', bg: 'bg-gray-100' },
                { label: '維修', value: maintenance, percent: getPercent(maintenance), color: 'text-yellow-600', bg: 'bg-yellow-50' },
            ];
        });
    }

    // Widget 2: Latest Alarms
    getLatestAlarms(devices: Signal<Device[]>) {
        return computed(() => {
            return devices()
                .filter(d => d.status === 'ALARM')
                .map(d => ({
                    deviceId: d.id,
                    type: d.type,
                    icon: getDeviceIcon(d.type),
                    color: 'bg-red-500',
                    title: `${d.name} 告警`,
                    desc: `監測數據異常 - ${d.code}`,
                    location: `${d.floor}F`,
                    time: new Date().toLocaleTimeString('zh-TW', { hour12: false })
                }));
        });
    }

    // Filter Options for Dashboard header
    getFilterOptions(floorDevices: Signal<Device[]>) {
        return computed(() => {
            const devices = floorDevices();
            const counts = devices.reduce((acc, d) => {
                acc[d.type] = (acc[d.type] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);

            return [
                { type: 'AC', label: '空調', icon: 'ci-air-conditioner', count: counts['AC'] || 0 },
                { type: 'FAN', label: '風機', icon: 'ci-fan', count: counts['FAN'] || 0 },
                { type: 'LIGHT', label: '照明', icon: 'ci-lighting', count: counts['LIGHT'] || 0 },
                { type: 'CCTV', label: 'CCTV', icon: 'ci-cctv', count: counts['CCTV'] || 0 },
                { type: 'MEETING', label: '會議室', icon: 'ci-meet-room', count: (counts['RB'] || 0) + (counts['PANEL'] || 0) + (counts['HOST'] || 0) },
                { type: 'DOOR', label: '門禁', icon: 'ci-access-control', count: counts['DOOR'] || 0 },
                { type: 'POWER', label: '電力', icon: 'ci-electricity', count: counts['POWER'] || 0 },
                { type: 'AIR_QUALITY', label: '空氣品質', icon: 'ci-air-quality', count: counts['AIR_QUALITY'] || 0 },
            ];
        });
    }

    // Group control data computation
    getGroupControlData(devices: Signal<Device[]>) {
        return computed(() => {
            const devs = devices();
            const floorsMap = new Map<number, any[]>();

            devs.forEach(d => {
                if (!floorsMap.has(d.floor)) floorsMap.set(d.floor, []);
                floorsMap.get(d.floor)!.push(d);
            });

            const categoriesList = [
                { type: 'LIGHT', name: '照明設備', icon: 'ci ci-lighting' },
                { type: 'AC', name: '冷氣機設備', icon: 'ci ci-air-conditioner' },
                { type: 'FAN', name: '全熱交換器設備', icon: 'ci ci-fan' },
            ];

            const result = Array.from(floorsMap.keys()).sort().map(floorNum => {
                const floorDevs = floorsMap.get(floorNum)!;

                const parsedDevices = categoriesList.map(cat => {
                    const catDevs = floorDevs.filter(d => d.type === cat.type);
                    if (catDevs.length === 0) return null;

                    const isDeviceOn = (d: Device) => {
                        // 如果設備離線，就當作沒運轉
                        if (d.status === 'OFFLINE' || !d.data) return false;
                        
                        switch (d.type) {
                            case 'AC':
                                // 冷氣的判斷標準：找 Power 電源點（key 以 Power 開頭），value==1 代表開啟
                                return Object.entries(d.data).some(([key, pt]) => key.startsWith('Power') && pt.value == 1);
                            case 'LIGHT':
                                // 照明的判斷標準：只要數據中任何一個以 Point 開頭的點位且 value==1，即為開機狀態
                                return Object.entries(d.data).some(([key, pt]) => 
                                    key.toLowerCase().startsWith('point') && pt.value == 1
                                );
                            case 'FAN':
                                // 全熱交換器 (風機) 的判斷標準：找 01_Power 點位且 value==1 代表開啟
                                return d.data['01_Power']?.value == 1;
                            default:
                                // 其他設備，看是否有任何運轉類型 (type=2) 的點處於開啟 (value=1) 的狀態
                                return Object.values(d.data).some(pt => pt.type === 2 && pt.value == 1);
                        }
                    };

                    const activeCount = catDevs.filter(isDeviceOn).length;
                    const active = activeCount > 0;

                    return {
                        name: cat.name,
                        categoryType: cat.type,
                        status: active ? '運行中' : '已關閉',
                        icon: cat.icon,
                        active: active,
                        color: active ? 'bg-emerald-500' : 'bg-slate-400',
                        bgClass: active ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                    };
                }).filter(Boolean) as any[];

                const activeFloor = parsedDevices.some(d => d.active);

                return {
                    floor: `${floorNum}F`,
                    active: activeFloor,
                    devices: parsedDevices
                };
            });
            return result;
        });
    }


    // Static Data
    get powerChartData() {
        return {
            labels: ['昨日全天', '今日預測'],
            datasets: [
                {
                    label: '用電量 (kWh)',
                    data: [12000, 4500],
                    backgroundColor: ['#9ca3af', '#eab308'],
                    borderRadius: 4,
                    barPercentage: 0.5
                }
            ]
        };
    }

    get contractCapacity() {
        return {
            current: 420,
            max: 600,
            percent: 70,
            status: 'NORMAL' // NORMAL, WARNING, CRITICAL
        };
    }

    getAirQualityData(devices: Signal<Device[]>) {
        return computed(() => {
            const aqDevice = devices().find(d => d.type === 'AIR_QUALITY');
            if (aqDevice && aqDevice.data) {
                const formatTime = (ts: number | undefined) => {
                    if (!ts) return '-';
                    return new Date(ts).toLocaleString('sv-SE').replace('T', ' '); // YYYY-MM-DD HH:mm:ss format
                };
                return {
                    time: formatTime(aqDevice.data['01_Temp']?.timestamp || aqDevice.data['01_CO2']?.timestamp),
                    temperature: { value: aqDevice.data['01_Temp']?.value ?? '-', isAlarm: !!aqDevice.data['01_Temp']?.isAlarm },
                    humidity: { value: aqDevice.data['01_RH']?.value ?? '-', isAlarm: !!aqDevice.data['01_RH']?.isAlarm },
                    co2: { value: aqDevice.data['01_CO2']?.value ?? '-', isAlarm: !!aqDevice.data['01_CO2']?.isAlarm },
                    pm25: { value: aqDevice.data['01_PM2.5']?.value ?? '-', isAlarm: !!aqDevice.data['01_PM2.5']?.isAlarm }
                };
            }
            return {
                time: '-',
                temperature: { value: '-', isAlarm: false },
                humidity: { value: '-', isAlarm: false },
                co2: { value: '-', isAlarm: false },
                pm25: { value: '-', isAlarm: false }
            };
        });
    }

    get meetingRooms() {
        return [
            { name: '大會議室', status: 'OCCUPIED', user: '業務部會議', until: '11:00' },
            { name: '會議室 A', status: 'AVAILABLE' },
            { name: '會議室 B', status: 'AVAILABLE' },
        ];
    }
}
